import { RateQuote, RateShop, RateUsage } from '../../rate';

import { RateQuoteGroupType } from './RateQuoteGroupType';

export class ProposedShipmentRateResponse {
  shipmentIdentifier: number;
  rateShop: RateShop;

  constructor(opts?: Partial<ProposedShipmentRateResponse>) {
    if (opts?.shipmentIdentifier) {
      this.shipmentIdentifier = opts.shipmentIdentifier;
    } else {
      throw new Error(
        'Invalid ProposedShipmentRateResponse - shipmentIdentifier is required',
      );
    }

    if (opts?.rateShop) {
      // Wrap in RateShop so the rateQuotes getter excludes errored carriers (a raw assignment leaks
      // them into the priced groups and lowest-cost comparison, where their 0 price sorts to the front).
      this.rateShop = new RateShop(opts.rateShop);
    } else {
      throw new Error(
        'Invalid ProposedShipmentRateResponse - rateShop is required',
      );
    }
  }

  get primaryRateShopQuotes(): RateQuote[] {
    return (
      this.rateShop.rateQuotes?.filter(
        (r) => r.rateUsage === RateUsage.RATE_SHOP,
      ) ?? []
    );
  }

  get preferredQuote() {
    return this.primaryRateShopQuotes[0];
  }

  get quotesOrderedByTransitTime() {
    return [...this.primaryRateShopQuotes].sort((a, b) => {
      const aTransitTime = a.transitTime ?? Number.MAX_SAFE_INTEGER;
      const bTransitTime = b.transitTime ?? Number.MAX_SAFE_INTEGER;
      return aTransitTime - bTransitTime;
    });
  }

  get fastestQuote() {
    return this.quotesOrderedByTransitTime[0];
  }

  get quotesOrderedByPrice() {
    return [...this.primaryRateShopQuotes].sort((a, b) => {
      const aTotalPrice = a.totalPrice ?? Number.MAX_SAFE_INTEGER;
      const bTotalPrice = b.totalPrice ?? Number.MAX_SAFE_INTEGER;
      return aTotalPrice - bTotalPrice;
    });
  }

  get cheapestQuote() {
    return this.quotesOrderedByPrice[0];
  }

  getQuoteForType(type: RateQuoteGroupType) {
    if (type === 'none') {
      throw new Error(`Cannot get a rate quote for 'none' type`);
    }

    const rateQuoteGroupKeys: Record<
      Exclude<RateQuoteGroupType, 'none'>,
      'preferredQuote' | 'fastestQuote' | 'cheapestQuote'
    > = {
      preferred: 'preferredQuote',
      fastest: 'fastestQuote',
      cheapest: 'cheapestQuote',
    };
    const rateQuoteGroupKey = rateQuoteGroupKeys[type];
    return this[rateQuoteGroupKey];
  }
}
